import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageTitle from "../components/PageTitle";

// The wording follows the Pilot's Guide (vWZZ-AVIOFP-PG-001). Where the guide names a thing,
// it is named the same way here.
const sections = [
  {
    title: "Briefing and airports",
    text: "Overview summarises the selected sector from the loaded release. Flightbox holds the OFP and any documents or messages published for the flight or the fleet. Airports is the primary source of vWZZ airport briefings.",
    items: [
      {
        src: "/images/aviofp/airports.png",
        title: "Airports",
        caption:
          "Briefings follow the layout published in the Airport Reference Publication, with WARNING and CAUTION information kept in its own boxes. The airports on your flight come first, identified as departure, destination or alternate; any other can be searched by ICAO, IATA or name and pinned to your account.",
      },
      {
        src: "/images/aviofp/flights.png",
        title: "Flights",
        caption:
          "Built from your vAMSYS bookings and the SimBrief OFPs linked to them. Each card carries the flight number and registration, the loaded release, STD and STA from the booking and the current ETD and ETA, marked ON TIME, DELAYED or CANCELLED.",
      },
      {
        src: "/images/aviofp/flightbox.png",
        title: "Flightbox",
        caption:
          "Messages and attachments, with unread material marked. The first message carries the printed OFP for the sector. Documents open in the viewer, which supports search, pinch-to-zoom and handwritten annotations saved with the flight.",
      },
    ],
  },
  {
    title: "Fuel, weights and the navlog",
    text: "Fuel and Weights is the first OFP page and where the loaded release is reviewed and accepted. The navigation log then tracks the sector as flown, and confirming waypoints updates the fuel and arrival estimates.",
    items: [
      {
        src: "/images/aviofp/fuel-weights.png",
        title: "Fuel and weights",
        caption:
          "Planned figures sit beside the ones you revise, with the equivalent endurance for final reserve and discretionary fuel and the structural limit against each weight. Alternates can be changed from the OFP's own list. ACCEPT OFP RLS records the acceptance, and a revised figure needs a reason.",
      },
      {
        src: "/images/aviofp/navlog.png",
        title: "Navlog",
        caption:
          "Every waypoint carries its MORA, planned flight level, overflight time and fuel on board against what you confirm in flight, with the trend beside each. Landing time, fuel and weight update from the last confirmed waypoint.",
      },
      {
        src: "/images/aviofp/direct.png",
        title: "Direct and divert",
        caption:
          "Hold a row for Direct, Divert, Edit route, Add note or Confirm waypoint. A direct offers the fixes after the last confirmed one, identifies the fixes bypassed and recalculates the estimates for the shorter route.",
      },
    ],
  },
];

function AviofpFigure({ item, onOpen }) {
  return (
    <figure className="etlb-figure">
      <button
        type="button"
        className="etlb-figure-image"
        onClick={() => onOpen(item)}
        aria-label={`Enlarge ${item.title}`}
      >
        <img src={item.src} alt={item.title} loading="lazy" decoding="async" />
      </button>
      <figcaption>
        <strong>{item.title}</strong>
        <span>{item.caption}</span>
      </figcaption>
    </figure>
  );
}

export default function AviofpPage() {
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    if (!selectedImage) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setSelectedImage(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [selectedImage]);

  return (
    <>
      <PageTitle>vWZZ - Aviofp</PageTitle>
      <div className="page-container etlb-page aviofp-public-page">
        <header className="etlb-page-header">
          <h1 className="page-title"><span className="brand">vWZZ</span> Aviofp</h1>
        </header>

        <section className="etlb-introduction" aria-labelledby="aviofp-intro-title">
          <div>
            <h2 id="aviofp-intro-title">Overview</h2>
            <p>
              Aviofp is the vWZZ flight operations application. It provides the
              operational flight plan, the related briefing material, the
              navigation log, the journey log and the airport briefings for each
              sector. You select your flight, review the briefing, accept the OFP
              and submit the flight records.
            </p>
            <p>
              The flight list is built from your vAMSYS bookings and the SimBrief
              OFPs linked to them. Aviofp runs in a browser and is designed for
              iPad in landscape; on a desktop it appears inside an iPad-sized
              frame.
            </p>
          </div>
          <img
            src="/images/aviofp/overview.png"
            alt="The Aviofp Overview page for a selected sector"
          />
        </section>

        {sections.map((section) => (
          <section className="etlb-detail-section" key={section.title}>
            <header>
              <div>
                <h2>{section.title}</h2>
                <p>{section.text}</p>
              </div>
            </header>
            <div className={`etlb-gallery etlb-gallery-${section.items.length}`}>
              {section.items.map((item) => (
                <AviofpFigure
                  item={item}
                  key={item.src}
                  onOpen={setSelectedImage}
                />
              ))}
            </div>
          </section>
        ))}

        <section className="etlb-closing">
          <div>
            <h2>Flight simulation only</h2>
            <p>
              Aviofp is open to any vWZZ pilot who wants it, and is not required
              to fly with us. It is built for flight simulation. Do not use it
              for real world flying.
            </p>
          </div>
          <Link className="btn btn-primary" to="/apply">
            Join <span className="brand">vWZZ</span>
          </Link>
        </section>

        {selectedImage && (
          <div
            className="etlb-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={`${selectedImage.title} full-size image`}
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) setSelectedImage(null);
            }}
          >
            <button
              type="button"
              className="etlb-lightbox-close"
              onClick={() => setSelectedImage(null)}
              aria-label="Close full-size image"
              autoFocus
            >
              Close
            </button>
            <figure>
              <img src={selectedImage.src} alt={selectedImage.title} />
              <figcaption>
                <strong>{selectedImage.title}</strong>
                <span>{selectedImage.caption}</span>
              </figcaption>
            </figure>
          </div>
        )}
      </div>
    </>
  );
}
